import { Reducer } from 'redux';
import t from './asset.types';
import type {
  Asset,
  AssetFilterPatch,
  AssetFilters,
  AssetLocationSection,
  AssetMasterOptionsState,
  AssetsState,
  ViewMode,
  InfoPanelMode,
} from '../types';

export type AssetAction =
  | { type: typeof t.SET_LOADING; payload: boolean }
  | { type: typeof t.SET_ERROR; payload: string | null }
  | { type: typeof t.FETCH_ASSETS_SUCCESS; payload: { assets: Asset[]; total: number; page: number; limit: number } }
  | { type: typeof t.LOAD_MORE_ASSETS_SUCCESS; payload: { assets: Asset[]; total: number; page: number; limit: number } }
  | { type: typeof t.UPLOAD_ASSET_SUCCESS; payload: Asset }
  | { type: typeof t.UPDATE_ASSET_SUCCESS; payload: Asset }
  | { type: typeof t.DELETE_ASSET_SUCCESS; payload: { assetId: string } }
  | { type: typeof t.BULK_DELETE_SUCCESS; payload: { assetIds: string[] } }
  | { type: typeof t.DUPLICATE_ASSET_SUCCESS; payload: Asset }
  | { type: typeof t.SET_FILTER; payload: AssetFilterPatch }
  | { type: typeof t.RESET_FILTERS }
  | { type: typeof t.SET_PAGE; payload: number }
  | { type: typeof t.SET_VIEW_MODE; payload: ViewMode }
  | { type: typeof t.TOGGLE_SELECT_ASSET; payload: string }
  | { type: typeof t.SELECT_ALL_ASSETS; payload: string[] }
  | { type: typeof t.CLEAR_SELECTION }
  | { type: typeof t.SET_ACTIVE_ASSET; payload: string | null }
  | { type: typeof t.SET_UPLOAD_PANEL_OPEN; payload: boolean }
  | { type: typeof t.SET_INFO_PANEL_OPEN; payload: boolean }
  | { type: typeof t.SET_DOWNLOAD_PANEL_OPEN; payload: boolean }
  | { type: typeof t.SET_SHARE_PANEL_OPEN; payload: boolean }
  | { type: typeof t.SET_LOCATIONS_PANEL_OPEN; payload: boolean }
  | { type: typeof t.SET_COMMENTS_PANEL_OPEN; payload: boolean }
  | { type: typeof t.SET_INFO_DETAILS_PANEL_OPEN; payload: boolean }
  | { type: typeof t.SET_INFO_PANEL_MODE; payload: InfoPanelMode }
  | { type: typeof t.FETCH_ASSET_LOCATIONS_START }
  | { type: typeof t.FETCH_ASSET_LOCATIONS_SUCCESS; payload: AssetLocationSection[] }
  | { type: typeof t.FETCH_ASSET_LOCATIONS_ERROR; payload: string }
  | { type: typeof t.FETCH_MASTER_OPTIONS_START }
  | { type: typeof t.FETCH_MASTER_OPTIONS_SUCCESS; payload: Omit<AssetMasterOptionsState, 'loading' | 'loaded' | 'error'> }
  | { type: typeof t.FETCH_MASTER_OPTIONS_ERROR };

const initialState: AssetsState = {
  assets: [],
  total: 0,
  page: 1,
  limit: 16,
  loading: false,
  error: null,
  filters: {},
  viewMode: 'grid',
  selectedIds: [],
  activeAssetId: null,
  uploadPanelOpen: false,
  infoPanelOpen: false,
  infoPanelMode: 'view',
  downloadPanelOpen: false,
  sharePanelOpen: false,
  locationsPanelOpen: false,
  commentsPanelOpen: false,
  infoDetailsPanelOpen: false,
  assetLocationSections: [],
  assetLocationsLoading: false,
  assetLocationsError: null,
  masterOptions: {
    brands: [],
    subBrands: [],
    licensors: [],
    products: [],
    users: [],
    folders: [],
    loading: false,
    loaded: false,
    error: false,
  },
};

const reducer: Reducer<AssetsState, AssetAction> = (state = initialState, action) => {
  switch (action.type) {
    case t.SET_LOADING:
      return { ...state, loading: action.payload };

    case t.SET_ERROR:
      return { ...state, error: action.payload, loading: false };

    case t.FETCH_ASSETS_SUCCESS:
      return {
        ...state,
        assets: action.payload.assets,
        total: action.payload.total,
        page: action.payload.page,
        limit: action.payload.limit,
        loading: false,
        error: null,
      };

    case t.LOAD_MORE_ASSETS_SUCCESS:
      return {
        ...state,
        assets: [...state.assets, ...action.payload.assets],
        total: action.payload.total,
        page: action.payload.page,
        limit: action.payload.limit,
        loading: false,
        error: null,
      };

    case t.UPLOAD_ASSET_SUCCESS:
      return {
        ...state,
        assets: [action.payload, ...state.assets],
        total: state.total + 1,
      };

    case t.UPDATE_ASSET_SUCCESS:
      return {
        ...state,
        assets: state.assets.map((a) => (a._id === action.payload._id ? action.payload : a)),
      };

    case t.DELETE_ASSET_SUCCESS:
      return {
        ...state,
        assets: state.assets.filter((a) => a._id !== action.payload.assetId),
        selectedIds: state.selectedIds.filter((id) => id !== action.payload.assetId),
        total: Math.max(0, state.total - 1),
      };

    case t.BULK_DELETE_SUCCESS: {
      const ids = new Set(action.payload.assetIds);
      return {
        ...state,
        assets: state.assets.filter((a) => !ids.has(a._id)),
        selectedIds: state.selectedIds.filter((id) => !ids.has(id)),
        total: Math.max(0, state.total - ids.size),
      };
    }

    case t.DUPLICATE_ASSET_SUCCESS:
      return {
        ...state,
        assets: [action.payload, ...state.assets],
        total: state.total + 1,
      };

    case t.SET_FILTER:
      return {
        ...state,
        filters: { ...state.filters, ...action.payload } as AssetsState['filters'],
        page: 1,
      };

    case t.RESET_FILTERS: {
      const { brandIds, subBrandId, licensorIds, licenseIds, likenessIds } = state.filters;
      const filters: AssetFilters = {};
      if (brandIds !== undefined) filters.brandIds = brandIds;
      if (subBrandId !== undefined) filters.subBrandId = subBrandId;
      if (licensorIds !== undefined) filters.licensorIds = licensorIds;
      if (licenseIds !== undefined) filters.licenseIds = licenseIds;
      if (likenessIds !== undefined) filters.likenessIds = likenessIds;
      return { ...state, filters, page: 1 };
    }

    case t.SET_PAGE:
      return { ...state, page: action.payload };

    case t.SET_VIEW_MODE:
      return { ...state, viewMode: action.payload };

    case t.TOGGLE_SELECT_ASSET:
      return {
        ...state,
        selectedIds: state.selectedIds.includes(action.payload)
          ? state.selectedIds.filter((id) => id !== action.payload)
          : [...state.selectedIds, action.payload],
      };

    case t.SELECT_ALL_ASSETS:
      return { ...state, selectedIds: action.payload };

    case t.CLEAR_SELECTION:
      return { ...state, selectedIds: [] };

    case t.SET_ACTIVE_ASSET:
      return { ...state, activeAssetId: action.payload };

    case t.SET_UPLOAD_PANEL_OPEN:
      return { ...state, uploadPanelOpen: action.payload };

    case t.SET_INFO_PANEL_OPEN:
      return { ...state, infoPanelOpen: action.payload, infoPanelMode: 'view' };

    case t.SET_INFO_PANEL_MODE:
      return { ...state, infoPanelMode: action.payload };

    case t.SET_DOWNLOAD_PANEL_OPEN:
      return { ...state, downloadPanelOpen: action.payload };

    case t.SET_SHARE_PANEL_OPEN:
      return { ...state, sharePanelOpen: action.payload };

    case t.SET_LOCATIONS_PANEL_OPEN:
      return { ...state, locationsPanelOpen: action.payload };

    case t.SET_COMMENTS_PANEL_OPEN:
      return { ...state, commentsPanelOpen: action.payload };

    case t.SET_INFO_DETAILS_PANEL_OPEN:
      return { ...state, infoDetailsPanelOpen: action.payload };

    case t.FETCH_ASSET_LOCATIONS_START:
      return { ...state, assetLocationsLoading: true, assetLocationsError: null };

    case t.FETCH_ASSET_LOCATIONS_SUCCESS:
      return { ...state, assetLocationSections: action.payload, assetLocationsLoading: false, assetLocationsError: null };

    case t.FETCH_ASSET_LOCATIONS_ERROR:
      return { ...state, assetLocationsLoading: false, assetLocationsError: action.payload };

    case t.FETCH_MASTER_OPTIONS_START:
      return { ...state, masterOptions: { ...state.masterOptions, loading: true, error: false } };

    case t.FETCH_MASTER_OPTIONS_SUCCESS:
      return { ...state, masterOptions: { ...action.payload, loading: false, loaded: true, error: false } };

    case t.FETCH_MASTER_OPTIONS_ERROR:
      return { ...state, masterOptions: { ...state.masterOptions, loading: false, loaded: false, error: true } };

    default:
      return state;
  }
};

export default reducer;
